<script setup>
import colorConstants from '@/shared/utils/color-constants.js'
import iconPosConstants from '@/shared/utils/icon-pos-constants.js'
import sizeConstants from '@/shared/utils/size-constants.js'

const props = defineProps({
  label: {
    type: String,
    default: null,
  },
  kind: {
    type: String,
    default: 'primary',
  },
  loading: Boolean,
  link: Boolean,
  icon: { type: String, default: null },
  iconPos: {
    type: [null, String],
    default: null,
    validator(value) {
      return value === null || iconPosConstants.includes(value)
    },
  },
  severity: {
    type: [null, String],
    default: null,
    validator(value) {
      return value === null || colorConstants.includes(value)
    },
  },
  disabled: Boolean,
  raised: Boolean,
  rounded: Boolean,
  text: Boolean,
  outlined: Boolean,
  badge: {
    type: [String, Number],
    default: '',
  },
  badgeSeverity: {
    type: [null, String],
    default: null,
    validator(value) {
      return value === null || colorConstants.includes(value)
    },
  },
  size: {
    type: [null, String],
    default: 'small',
    validator(value) {
      return value === null || sizeConstants.includes(value)
    },
  },
  customClass: {
    type: String,
    default: '',
  },
})

defineEmits(['click'])

const buttonStyles = {
  primary: {
    bg: 'bg-dss-primary-500',
    text: 'text-white',
    outlined: 'bg-transparent! text-dss-primary-500 border-dss-primary-500',
  },
  secondary: {
    bg: 'bg-dss-secondary-500 dark:bg-dss-secondary-400',
    text: 'text-white',
    outlined: 'text-dss-secondary-500 border-dss-secondary-500',
  },
  success: {
    bg: 'bg-dss-success-500',
    text: 'text-dss-success-50',
    outlined: 'bg-transparent text-dss-success-500 border-dss-success-500',
  },
  info: {
    bg: 'bg-dss-info-500',
    text: 'text-dss-info-50',
    outlined: 'text-dss-info-500 border-dss-info-500',
  },
  warn: {
    bg: 'bg-dss-warning-500',
    text: 'text-dss-warning-50',
    outlined: 'text-dss-warning-500 border-dss-warning-500',
  },
  help: {
    bg: 'bg-purple-500',
    text: 'text-purple-50',
    outlined: 'text-purple-500 border-purple-500',
  },
  danger: {
    bg: 'bg-dss-error-500',
    text: 'text-dss-error-50',
    outlined: 'text-dss-error-500 border-dss-error-500',
  },
}
const buttonClasses = computed(() => {
  const styleType = props.kind || 'primary'
  const styles = buttonStyles[styleType] || buttonStyles.primary

  return props.outlined
    ? `bg-white ${styles.outlined}`
    : `${styles.bg} ${styles.text}`
})
</script>

<template>
  <Button
    v-bind="{ ...props, ...$props }"
    class="dss-button rounded-xl px-4 font-medium!"
    :class="[buttonClasses, props.customClass]"
    severity="dss"
    @click="$emit('click')"
  />
</template>

<style>
.dss-button {
  height: 42px;
  max-height: 42px;
  display: flex;
  align-items: center;
}
.dss-button span[data-pc-section=icon] {
  font-size: 16px;
}
.dss-button span[data-pc-section=label] {
  font-weight: 500 !important;
  font-size: 13px;
}
</style>
